import React from 'react';
import { Tooltip } from 'antd';

/**
 * 格式化时间显示
 * @param time 时间戳或时间字符串
 * @returns 格式化后的时间字符串
 */
export const formatTime = (time: number | string | null): string => {
  if (!time) return '-';

  try {
    let date: Date;

    // 处理字符串时间戳
    if (typeof time === 'string') {
      // 检查是否为纯数字字符串（时间戳）
      if (/^\d+$/.test(time)) {
        // 判断是秒级还是毫秒级时间戳
        const timestamp = parseInt(time, 10);
        date =
          timestamp > 1000000000000
            ? new Date(timestamp)
            : new Date(timestamp * 1000);
      } else {
        // 其他格式的字符串时间
        date = new Date(time);
      }
    } else {
      // 数字时间戳
      date = new Date(time);
    }

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return String(time);
    }

    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(time);
  }
};

/**
 * 获取权限类型文本
 * @param type 权限类型数字
 * @returns 权限类型文本
 */
export const getPermissionTypeText = (type: number): string => {
  const types = {
    1: '系统菜单',
    2: '按钮权限',
    3: '接口权限',
    4: '其他权限',
  };
  return types[type as keyof typeof types] || '未知';
};

/**
 * 获取权限类型颜色
 * @param type 权限类型数字
 * @returns 权限类型颜色
 */
export const getPermissionTypeColor = (type: number): string => {
  const colors = {
    1: 'green',
    2: 'blue',
    3: 'orange',
    4: 'purple',
  };
  return colors[type as keyof typeof colors] || 'default';
};

/**
 * 获取HTTP方法颜色
 * @param method HTTP方法
 * @returns 颜色
 */
export const getHttpMethodColor = (method: string): string => {
  const colors = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    DELETE: 'red',
  };
  return colors[method as keyof typeof colors] || 'default';
};

/**
 * 解析时间戳（支持字符串和数字格式）
 * @param timestamp 时间戳
 * @returns Date对象或null
 */
export const parseTimestamp = (
  timestamp: number | string | null
): Date | null => {
  if (!timestamp) return null;

  try {
    let date: Date;

    if (typeof timestamp === 'string') {
      if (/^\d+$/.test(timestamp)) {
        const num = parseInt(timestamp, 10);
        // 判断是秒级还是毫秒级时间戳
        date = num > 1000000000000 ? new Date(num) : new Date(num * 1000);
      } else {
        date = new Date(timestamp);
      }
    } else {
      date = new Date(timestamp);
    }

    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

/**
 * 格式化相对时间（如：3分钟前、2小时前）
 * @param time 时间戳或时间字符串
 * @returns 相对时间字符串
 */
export const formatRelativeTime = (time: number | string | null): string => {
  const date = parseTimestamp(time);
  if (!date) return '-';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (diff < minute) {
    return '刚刚';
  } else if (diff < hour) {
    return `${Math.floor(diff / minute)}分钟前`;
  } else if (diff < day) {
    return `${Math.floor(diff / hour)}小时前`;
  } else if (diff < week) {
    return `${Math.floor(diff / day)}天前`;
  } else if (diff < month) {
    return `${Math.floor(diff / week)}周前`;
  } else if (diff < year) {
    return `${Math.floor(diff / month)}个月前`;
  } else {
    return `${Math.floor(diff / year)}年前`;
  }
};

/**
 * 时间列渲染器 - 支持多种显示模式
 * @param time 时间戳或时间字符串
 * @param mode 显示模式：'full' | 'relative' | 'date' | 'datetime'
 * @returns 渲染后的时间显示
 */
export const renderTimeColumn = (
  time: number | string | null,
  mode: 'full' | 'relative' | 'date' | 'datetime' = 'full'
): string => {
  if (!time) return '-';

  const date = parseTimestamp(time);
  if (!date) return String(time);

  switch (mode) {
    case 'relative':
      return formatRelativeTime(time);
    case 'date':
      return date.toLocaleDateString('zh-CN');
    case 'datetime':
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    case 'full':
    default:
      return formatTime(time);
  }
};

/**
 * 创建时间列配置
 * @param mode 显示模式
 * @returns Ant Design 列配置
 */
export const createTimeColumn = (
  mode: 'full' | 'relative' | 'date' | 'datetime' = 'full'
) => ({
  title: '创建时间',
  dataIndex: 'createTime',
  key: 'createTime',
  render: (time: number | string | null) => renderTimeColumn(time, mode),
  sorter: (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const timeA =
      parseTimestamp(a.createTime as number | string | null)?.getTime() || 0;
    const timeB =
      parseTimestamp(b.createTime as number | string | null)?.getTime() || 0;
    return timeA - timeB;
  },
});

/**
 * 更新时间列配置
 * @param mode 显示模式
 * @returns Ant Design 列配置
 */
export const updateTimeColumn = (
  mode: 'full' | 'relative' | 'date' | 'datetime' = 'full'
) => ({
  title: '更新时间',
  dataIndex: 'updateTime',
  key: 'updateTime',
  render: (time: number | string | null) => renderTimeColumn(time, mode),
  sorter: (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const timeA =
      parseTimestamp(a.updateTime as number | string | null)?.getTime() || 0;
    const timeB =
      parseTimestamp(b.updateTime as number | string | null)?.getTime() || 0;
    return timeA - timeB;
  },
});

/**
 * 创建文本裁剪列配置
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @param maxWidth 最大宽度
 * @param showTooltip 是否显示tooltip
 * @returns Ant Design 列配置
 */
export const createEllipsisColumn = (
  title: string,
  dataIndex: string,
  maxLength: number = 20,
  maxWidth?: string | number,
  showTooltip: boolean = true
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: (text: string | number | null | undefined) => {
    if (!text) return '-';

    const textStr = String(text);
    const shouldTruncate = textStr.length > maxLength;
    const displayText = shouldTruncate
      ? `${textStr.slice(0, maxLength)}...`
      : textStr;

    const style = maxWidth
      ? {
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' as const,
        }
      : undefined;

    const content = React.createElement('span', { style }, displayText);

    if (!shouldTruncate || !showTooltip) {
      return content;
    }

    return React.createElement(
      Tooltip,
      {
        title: textStr,
        placement: 'topLeft',
        overlayStyle: { maxWidth: '300px' },
      },
      content
    );
  },
});

/**
 * 创建长文本列配置（默认裁剪）
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @returns Ant Design 列配置
 */
export const createLongTextColumn = (
  title: string,
  dataIndex: string,
  maxLength: number = 15
) => createEllipsisColumn(title, dataIndex, maxLength);

/**
 * 创建URL列配置
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @returns Ant Design 列配置
 */
export const createUrlColumn = (
  title: string,
  dataIndex: string,
  maxLength: number = 30
) => createEllipsisColumn(title, dataIndex, maxLength, '200px');

/**
 * 创建描述列配置
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @returns Ant Design 列配置
 */
export const createDescriptionColumn = (
  title: string,
  dataIndex: string,
  maxLength: number = 25
) => createEllipsisColumn(title, dataIndex, maxLength, '150px');

/**
 * 创建对象字段列配置 - 处理可能包含对象的长字段
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @param extractKeys 从对象中提取的键名数组，按优先级排序
 * @returns Ant Design 列配置
 */
export const createObjectFieldColumn = (
  title: string,
  dataIndex: string,
  maxLength: number = 15,
  extractKeys: string[] = ['name', 'roleName', 'title', 'label']
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: (value: string | object | null | undefined) => {
    if (!value) return '-';

    let displayText = '';

    if (typeof value === 'string') {
      displayText = value;
    } else if (value && typeof value === 'object') {
      // 尝试从对象中提取文本
      for (const key of extractKeys) {
        if (key in value && value[key as keyof typeof value]) {
          displayText = String(value[key as keyof typeof value]);
          break;
        }
      }

      // 如果没找到指定键，尝试其他常见键
      if (!displayText) {
        const commonKeys = ['id', 'value', 'text', 'content'];
        for (const key of commonKeys) {
          if (key in value && value[key as keyof typeof value]) {
            displayText = String(value[key as keyof typeof value]);
            break;
          }
        }
      }

      // 如果还是没找到，使用JSON字符串
      if (!displayText) {
        displayText = JSON.stringify(value);
      }
    } else {
      displayText = String(value);
    }

    const shouldTruncate = displayText.length > maxLength;
    const truncatedText = shouldTruncate
      ? `${displayText.slice(0, maxLength)}...`
      : displayText;

    const style = {
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    };

    const content = React.createElement(
      'span',
      {
        style,
      },
      truncatedText
    );

    if (shouldTruncate) {
      return React.createElement(
        Tooltip,
        {
          title: displayText,
          placement: 'topLeft',
          overlayStyle: { maxWidth: '300px' },
        },
        content
      );
    }

    return content;
  },
});

/**
 * 创建权限标识列配置 - 处理长权限列表
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxItems 最大显示权限数量
 * @returns Ant Design 列配置
 */
export const createPermissionKeysColumn = (
  title: string = '权限标识',
  dataIndex: string = 'permissionKeys',
  maxItems: number = 3
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: (keys: string | object | null) => {
    // 处理不同类型的权限数据
    let permissionArray: string[] = [];

    if (typeof keys === 'string') {
      permissionArray = keys.split(',');
    } else if (Array.isArray(keys)) {
      permissionArray = keys;
    } else if (keys && typeof keys === 'object') {
      // 如果是对象，尝试提取权限信息
      permissionArray = Object.keys(keys);
    }

    if (permissionArray.length === 0) {
      return React.createElement('span', {}, '-');
    }

    const shouldTruncate = permissionArray.length > maxItems;
    const displayItems = shouldTruncate
      ? permissionArray.slice(0, maxItems)
      : permissionArray;
    const remainingCount = permissionArray.length - maxItems;

    const tags = displayItems.map((key, index) =>
      React.createElement(
        'span',
        {
          key: index,
          style: {
            color: '#1890ff',
            backgroundColor: '#f0f5ff',
            border: '1px solid #d6e4ff',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '12px',
            margin: '2px',
            display: 'inline-block',
          },
        },
        key
      )
    );

    if (shouldTruncate) {
      const moreTag = React.createElement(
        'span',
        {
          style: {
            color: '#666',
            backgroundColor: '#f5f5f5',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '12px',
            margin: '2px',
            display: 'inline-block',
          },
        },
        `+${remainingCount}`
      );

      const fullText = permissionArray.join(', ');

      return React.createElement(
        Tooltip,
        {
          title: fullText,
          placement: 'topLeft',
          overlayStyle: { maxWidth: '400px' },
        },
        React.createElement('span', {}, ...tags, moreTag)
      );
    }

    return React.createElement('div', {}, ...tags);
  },
});

/**
 * 创建角色列配置 - 处理角色ID到角色名称的映射
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @param roleMap 角色ID到角色名称的映射
 * @returns Ant Design 列配置
 */
export const createRoleColumn = (
  title: string = '角色',
  dataIndex: string = 'role',
  maxLength: number = 8,
  roleMap: Record<string, string> = {
    '1': '超级管理员',
    '2': '普通用户',
    '3': '访客',
    admin: '超级管理员',
    user: '普通用户',
    guest: '访客',
  }
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: (role: string | object | null | undefined) => {
    if (!role) return '-';

    let roleText = '';

    if (typeof role === 'string') {
      // 尝试从映射中获取角色名称
      roleText = roleMap[role] || role;
    } else if (role && typeof role === 'object') {
      // 如果是对象，尝试提取角色名称
      roleText =
        (role as { roleName?: string }).roleName ||
        (role as { name?: string }).name ||
        (role as { title?: string }).title ||
        JSON.stringify(role);
    } else {
      roleText = String(role);
    }

    const shouldTruncate = roleText.length > maxLength;
    const displayText = shouldTruncate
      ? `${roleText.slice(0, maxLength)}...`
      : roleText;

    const style = {
      color: roleText === '超级管理员' ? '#cf1322' : '#1890ff',
      backgroundColor: roleText === '超级管理员' ? '#fff2f0' : '#f0f5ff',
      border: `1px solid ${roleText === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '12px',
      display: 'inline-block',
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const content = React.createElement('span', { style }, displayText);

    if (shouldTruncate) {
      return React.createElement(
        Tooltip,
        {
          title: roleText,
          placement: 'topLeft',
          overlayStyle: { maxWidth: '300px' },
        },
        content
      );
    }

    return content;
  },
});

/**
 * 创建动态角色列配置 - 支持动态角色映射
 * @param title 列标题
 * @param dataIndex 数据索引
 * @param maxLength 最大显示长度
 * @param getRoleName 获取角色名称的函数
 * @returns Ant Design 列配置
 */
export const createDynamicRoleColumn = (
  title: string = '角色',
  dataIndex: string = 'role',
  maxLength: number = 8,
  getRoleName: (roleId: string) => string = roleId => roleId
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: (role: string | object | null | undefined) => {
    if (!role) return '-';

    let roleText = '';

    if (typeof role === 'string') {
      // 使用动态函数获取角色名称
      roleText = getRoleName(role);
    } else if (role && typeof role === 'object') {
      // 如果是对象，尝试提取角色名称
      roleText =
        (role as { roleName?: string }).roleName ||
        (role as { name?: string }).name ||
        (role as { title?: string }).title ||
        JSON.stringify(role);
    } else {
      roleText = String(role);
    }

    const shouldTruncate = roleText.length > maxLength;
    const displayText = shouldTruncate
      ? `${roleText.slice(0, maxLength)}...`
      : roleText;

    const style = {
      color: roleText === '超级管理员' ? '#cf1322' : '#1890ff',
      backgroundColor: roleText === '超级管理员' ? '#fff2f0' : '#f0f5ff',
      border: `1px solid ${roleText === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '12px',
      display: 'inline-block',
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const content = React.createElement('span', { style }, displayText);

    if (shouldTruncate) {
      return React.createElement(
        Tooltip,
        {
          title: roleText,
          placement: 'topLeft',
          overlayStyle: { maxWidth: '300px' },
        },
        content
      );
    }

    return content;
  },
});
